import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AdminPanel from './components/AdminPanel';
import PasswordBypass from './components/PasswordBypass';
import ComingSoonDisplay from './components/ComingSoonDisplay';

const ComingSoonPage = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bypassGranted, setBypassGranted] = useState(false);

  // Check if user has admin role
  const isAdmin = userProfile?.role === 'admin';

  // Load coming soon settings
  const loadSettings = async () => {
    try {
      setError(null);
      const { data, error } = await supabase?.from('coming_soon_settings')?.select('*')?.limit(1)?.single();

      if (error) {
        if (error?.code === 'PGRST116') {
          // No settings found, create default
          setSettings({
            is_enabled: false,
            bypass_password: '',
            title: 'Coming Soon',
            description: 'We are working hard to bring you something amazing. Stay tuned!'
          });
        } else {
          setError('Failed to load settings: ' + error?.message);
        }
      } else {
        setSettings(data);
      }
    } catch (error) {
      setError('Network error loading settings: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  // Update settings (admin only)
  const updateSettings = async (updatedSettings) => {
    if (!isAdmin) return;

    try {
      setError(null);
      const { data, error } = await supabase?.from('coming_soon_settings')?.upsert([{
          ...updatedSettings,
          created_by: user?.id,
          updated_at: new Date()?.toISOString()
        }], {
          onConflict: 'id'
        })?.select()?.single();

      if (error) {
        setError('Failed to update settings: ' + error?.message);
      } else {
        setSettings(data);
      }
    } catch (error) {
      setError('Network error updating settings: ' + error?.message);
    }
  };

  // Handle password bypass
  const handleBypassSuccess = () => {
    setBypassGranted(true);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Show loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/10 to-primary/5 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !settings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/10 to-primary/5 flex items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <span className="text-destructive text-2xl">⚠</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Error Loading Page</h1>
          <p className="text-muted-foreground max-w-md">{error}</p>
          <button 
            onClick={loadSettings}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If coming soon is disabled, or user is admin, or bypass is granted, show admin panel
  if (!settings?.is_enabled || isAdmin || bypassGranted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/10 to-primary/5">
        {/* Admin Panel for admins */}
        {isAdmin && (
          <AdminPanel 
            settings={settings}
            onUpdateSettings={updateSettings}
            error={error}
          />
        )}
        
        {/* Success message for non-admin users who bypassed */}
        {!isAdmin && bypassGranted && (
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="text-center space-y-6 max-w-md">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                <span className="text-green-500 text-3xl">✓</span>
              </div>
              <h1 className="text-3xl font-bold text-foreground">Access Granted</h1>
              <p className="text-muted-foreground">
                You have successfully bypassed the coming soon page. The site is currently in development.
              </p>
              <a 
                href="/homepage"
                className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Continue to Site
              </a>
            </div>
          </div>
        )}
        
        {/* Preview mode message for admins when coming soon is disabled */}
        {isAdmin && !settings?.is_enabled && (
          <div className="bg-amber-500/10 border-l-4 border-amber-500 p-4">
            <p className="text-amber-700 dark:text-amber-300">
              <strong>Admin Preview:</strong> Coming soon mode is currently disabled. 
              Regular visitors can access the site normally.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Show coming soon page with password bypass option
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/10 to-primary/5">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Main Coming Soon Display */}
        <div className="flex-1 flex items-center justify-center p-4">
          <ComingSoonDisplay settings={settings} />
        </div>
        
        {/* Password Bypass Sidebar */}
        <div className="lg:w-80 bg-card/50 backdrop-blur-sm border-l border-border p-6 flex items-center justify-center">
          <PasswordBypass 
            settings={settings}
            onBypassSuccess={handleBypassSuccess}
          />
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;